import { useMemo, useState } from 'react';
import { useApp, useNow } from '@/app/AppContext';
import type { LocalEvent } from '@/lib/schemas';
import { addDaysToKey, localDayKey } from '@/lib/time';
import { mean, summarizeDays, type DaySummary } from './stats';

const number = (value: number) => new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(value);
const hours = (value: number) => `${number(value / 3_600_000)} h`;

function DailyChart({ title, days, value, format }: {
  title: string; days: DaySummary[]; value: (day: DaySummary) => number | null; format: (value: number) => string;
}) {
  const maximum = Math.max(1, ...days.filter((d) => d.tracked).map((d) => value(d) ?? 0));
  return <figure className="card space-y-2 p-4">
    <figcaption className="font-semibold">{title}</figcaption>
    <ul className="space-y-2" aria-label={`${title} daily chart`}>
      {days.map((day) => {
        const v = day.tracked ? value(day) : null;
        const label = !day.tracked ? 'No tracking' : v === null ? 'Not enough feeds' : format(v);
        return <li key={day.day} className="grid items-center gap-2 text-xs" style={{ gridTemplateColumns: '5rem minmax(0,1fr) 6rem' }}>
          <span>{day.day.slice(5)}</span>
          <span className="block h-3 rounded bg-ink-800" aria-hidden="true">
            {v !== null && <span className="block h-3 rounded bg-accent" style={{ width: `${100 * v / maximum}%`, backgroundColor: 'currentColor' }} />}
          </span>
          <span className="text-right num">{label}</span>
        </li>;
      })}
    </ul>
  </figure>;
}

/** Previous complete local days, scoped defensively to the selected baby. */
export function Analytics({ events }: { events: LocalEvent[] }) {
  const { baby, household, tz } = useApp();
  const now = useNow(60_000);
  const [period, setPeriod] = useState(7);
  const today = localDayKey(now, tz);
  const first = addDaysToKey(today, -period);
  const last = addDaysToKey(today, -1);
  const days = useMemo(() => summarizeDays(events.filter((e) => e.baby_id === baby?.id && e.household_id === household?.id), tz, first, last, now), [events, baby?.id, household?.id, tz, first, last, now]);
  const tracked = days.filter((d) => d.tracked);
  if (!baby) return <p>Create a baby profile to view Analytics.</p>;
  return <section className="space-y-4" aria-labelledby="analytics-heading">
    <div className="card space-y-3 p-4">
      <h1 id="analytics-heading" className="text-xl font-semibold">Analytics</h1>
      <p>{baby.display_name} · {tz}</p>
      <label className="label" htmlFor="analytics-period">Period</label>
      <select className="input" id="analytics-period" value={period} onChange={(e) => setPeriod(Number(e.target.value))}>
        <option value={7}>Previous 7 complete days</option><option value={14}>Previous 14 complete days</option>
      </select>
      <p className="text-sm">{first} to {last}. Today is excluded.</p>
      <p className="text-sm text-ink-300">No tracking means no records of any type that day. Zero means none recorded on a day with some tracking; it does not prove no events occurred. Mixed diapers count in both wet and dirty totals. Naps count only sessions marked as naps, on their start day; sleep time is split at local midnight.</p>
      {tracked.length === 0 ? <p role="status">No tracking in this period.</p> : <p role="status">{tracked.length} of {period} days tracked. Average daily formula: {number(mean(tracked.map((d) => d.formulaMl))!)} ml. Average daily sleep: {hours(mean(tracked.map((d) => d.sleepMs))!)}. Averages exclude days with no tracking.</p>}
    </div>
    <div className="grid gap-4 md:grid-cols-2">
      <DailyChart title="Formula volume" days={days} value={(d) => d.formulaMl} format={(v) => `${number(v)} ml`} />
      <DailyChart title="Mean feed interval" days={days} value={(d) => d.meanFeedIntervalMin} format={(v) => `${number(v)} min`} />
      <DailyChart title="Naps" days={days} value={(d) => d.napCount} format={(v) => number(v)} />
      <DailyChart title="Total sleep" days={days} value={(d) => d.sleepMs} format={hours} />
      <DailyChart title="Diapers" days={days} value={(d) => d.diaperCount} format={(v) => number(v)} />
    </div>
    <div className="card overflow-x-auto p-4" tabIndex={0} role="region" aria-label="Daily analytics table">
      <table className="w-full text-left text-sm" style={{ minWidth: '43rem' }}>
        <caption className="mb-2 text-left font-semibold">Daily totals and intervals</caption>
        <thead><tr>{['Date', 'Formula (ml)', 'Feeds', 'Mean interval (min)', 'Naps', 'Sleep', 'Diapers', 'Wet', 'Dirty'].map((h) => <th className="p-2" scope="col" key={h}>{h}</th>)}</tr></thead>
        <tbody>{days.map((d) => <tr key={d.day}>
          <th className="p-2" scope="row">{d.day}</th>
          {!d.tracked ? <td className="p-2" colSpan={8}>No tracking</td> : <>
            {[number(d.formulaMl), d.feedCount, d.meanFeedIntervalMin === null ? 'Not enough feeds' : number(d.meanFeedIntervalMin), d.napCount, hours(d.sleepMs), d.diaperCount, d.wet, d.dirty].map((v, i) => <td className="p-2 num" key={i}>{v}</td>)}
          </>}
        </tr>)}</tbody>
      </table>
    </div>
    <p className="text-xs text-ink-300">Feed intervals use consecutive feeds within each local day and need at least two feeds. Running sleep contributes elapsed time to each day it overlaps.</p>
  </section>;
}
